<template>
  <div>
    <el-dialog
      :width="dialogWidth"
      :title="`${dailyDetailObj.username || ''} 每日详情`"
      top="5vh"
      center
      :visible="dailyDetailObj.visible"
      @close="closeDialog()"
      id="daily-detail-dialog"
    >
      <div style="text-align: right; margin-bottom: 20px">
        <el-link
          type="success"
          :icon="chartMode ? 'el-icon-s-grid' : 'el-icon-data-analysis'"
          @click="chartMode = !chartMode"
          :disabled="dataList.length === 0"
          :underline="false"
          >切换至{{ chartMode ? "表格" : "图表" }}检视
        </el-link>
      </div>

      <template v-if="chartMode">
        <div class="summary-tags">
          <el-tag
            v-for="item in summaryOnChartMode"
            :key="item.name"
            effect="dark"
            size="mini"
            class="summary-tag"
          >
            {{ item.name }}:
            <span>{{
              ["firstAuditTime", "averageTime"].includes(item.prop)
                ? item.time
                : item.value
            }}</span>
          </el-tag>
        </div>
        <div
          ref="mainChart"
          :style="{
            width: '100%',
            height: '600px',
          }"
        />
      </template>

      <el-table
        v-else
        stripe
        ref="table"
        v-loading="dataLoading"
        element-loading-background="rgba(255, 255, 255, 0.8)"
        element-loading-spinner="el-icon-loading"
        :data="dataList"
        :header-cell-style="{ 'background-color': '#f5f7fa' }"
        :summary-method="getSummaries"
        show-summary
      >
        <!-- 代理数据 -->
        <template v-if="dailyDetailObj.type === 'agent'">
          <el-table-column width="120" label="时间" align="center">
            <template slot-scope="scope">
              {{ scope.row.date }}
            </template>
          </el-table-column>
          <el-table-column
            label="实际使用卡数"
            align="center"
            prop="userCardNum"
          >
            <template slot-scope="scope">
              {{ formatDecimalSeparator(scope.row.userCardNum, 4) }}
            </template>
          </el-table-column>
          <el-table-column label="总收款" align="center" prop="payAmount">
            <template slot-scope="scope">
              {{ formatDecimalSeparator(scope.row.payAmount, 4) }}
            </template>
          </el-table-column>
          <el-table-column label="总出款" align="center" prop="withdrawAmount">
            <template slot-scope="scope">
              {{ formatDecimalSeparator(scope.row.withdrawAmount, 4) }}
            </template>
          </el-table-column>
          <el-table-column label="总佣金" align="center" prop="rebateTotal">
            <template slot-scope="scope">
              {{ formatDecimalSeparator(scope.row.rebateTotal, 4) }}
            </template>
          </el-table-column>
        </template>

        <!-- 客服绩效数据 -->
        <template v-else-if="dailyDetailObj.type === 'kefu'">
          <el-table-column width="120" label="时间" align="center">
            <template slot-scope="scope">
              {{ scope.row.date }}
            </template>
          </el-table-column>
          <el-table-column label="总量" prop="totalCount">
            <template slot-scope="scope">{{ scope.row.totalCount }}</template>
          </el-table-column>
          <el-table-column label="总完成量" prop="completedCount">
            <template slot-scope="scope">{{
              scope.row.completedCount
            }}</template>
          </el-table-column>
          <el-table-column label="完成量大额" prop="completedBigCount">
            <template slot-scope="scope">{{
              scope.row.completedBigCount
            }}</template>
          </el-table-column>
          <el-table-column label="完成量小额" prop="completedSmallCount">
            <template slot-scope="scope">{{
              scope.row.completedSmallCount
            }}</template>
          </el-table-column>
          <el-table-column label="未完成量" prop="uncompletedCount">
            <template slot-scope="scope">{{
              scope.row.uncompletedCount
            }}</template>
          </el-table-column>
          <el-table-column label="失败量" prop="failedCount">
            <template slot-scope="scope">{{ scope.row.failedCount }}</template>
          </el-table-column>
          <el-table-column label="退款量" prop="refundCount">
            <template slot-scope="scope">{{ scope.row.refundCount }}</template>
          </el-table-column>
          <!-- <el-table-column label="审核前关闭量" prop="closedCount">
            <template slot-scope="scope">{{ scope.row.closedCount }}</template>
          </el-table-column> -->
          <el-table-column label="平均完成时间" prop="averageTime">
            <template slot-scope="scope">{{
              formatDuration(scope.row.averageTime)
            }}</template>
          </el-table-column>
          <el-table-column label="金额" prop="amount">
            <template slot-scope="scope">{{
              formatDecimalSeparator(scope.row.amount, 4)
            }}</template>
          </el-table-column>
        </template>

        <!-- 卡主数据 -->
        <template v-else-if="dailyDetailObj.type === 'customer'">
          <el-table-column width="120" label="时间" align="center">
            <template slot-scope="scope">
              {{ scope.row.date }}
            </template>
          </el-table-column>
          <el-table-column label="总流水" prop="transactionTotal">
            <template slot-scope="scope">{{
              formatDecimalSeparator(scope.row.transactionTotal, 4)
            }}</template>
          </el-table-column>
          <el-table-column label="收款" prop="payAmount">
            <template slot-scope="scope">{{
              formatDecimalSeparator(scope.row.payAmount, 4)
            }}</template>
          </el-table-column>
          <el-table-column label="付款" prop="withdrawAmount">
            <template slot-scope="scope">{{
              formatDecimalSeparator(scope.row.withdrawAmount, 4)
            }}</template>
          </el-table-column>
          <el-table-column label="活动佣金" prop="specialActivityBalanceAmount">
            <template slot-scope="scope">{{
              formatDecimalSeparator(scope.row.specialActivityBalanceAmount, 4)
            }}</template>
          </el-table-column>
          <el-table-column label="订单佣金" prop="balanceAmount">
            <template slot-scope="scope">{{
              formatDecimalSeparator(scope.row.balanceAmount, 4)
            }}</template>
          </el-table-column>
          <el-table-column label="总佣金" prop="rebateTotal">
            <template slot-scope="scope">
              {{ formatDecimalSeparator(scope.row.rebateTotal, 4) }}
            </template>
          </el-table-column>
        </template>

        <!-- 审核员绩效数据 -->
        <template v-else-if="dailyDetailObj.type === 'credentialAuditor'">
          <el-table-column width="120" label="时间" align="center">
            <template slot-scope="scope">
              {{ scope.row.date }}
            </template>
          </el-table-column>
          <el-table-column label="总量" prop="totalCount">
            <template slot-scope="scope">{{ scope.row.totalCount }}</template>
          </el-table-column>
          <el-table-column label="确认出款" prop="completionCount">
            <template slot-scope="scope">{{
              scope.row.completionCount
            }}</template>
          </el-table-column>
          <el-table-column label="卡主未处理" prop="uncompletionCount">
            <template slot-scope="scope">{{
              scope.row.uncompletionCount
            }}</template>
          </el-table-column>
          <el-table-column label="确认未出款" prop="failCount">
            <template slot-scope="scope">{{ scope.row.failCount }}</template>
          </el-table-column>
          <el-table-column label="审核前关闭" prop="closeCount">
            <template slot-scope="scope">{{ scope.row.closeCount }}</template>
          </el-table-column>
          <el-table-column label="退款量" prop="refundCount">
            <template slot-scope="scope">{{ scope.row.refundCount }}</template>
          </el-table-column>
          <el-table-column label="首次审核平均时间" prop="firstAuditTime">
            <template slot-scope="scope">{{
              formatDuration(scope.row.firstAuditTime)
            }}</template>
          </el-table-column>
          <el-table-column label="平均完成时间" prop="averageTime">
            <template slot-scope="scope">{{
              formatDuration(scope.row.averageTime)
            }}</template>
          </el-table-column>
          <el-table-column label="离线次数" prop="logOutCount">
            <template slot-scope="scope">{{ scope.row.logOutCount }}</template>
          </el-table-column>
          <el-table-column label="转单次数" prop="transferCount">
            <template slot-scope="scope">{{
              scope.row.transferCount
            }}</template>
          </el-table-column>
        </template>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import mainCommon from "@/utils/mainCommon";
import { getAgentCountByDate } from "@/api/service";
import {
  getDailyStatisticsByKefu,
  getDailyStatisticsByCustomer,
  getDailyStatisticsByAuditor,
} from "@/api/statistics";
import echarts from "echarts";

export default {
  mixins: [mainCommon],
  props: ["dailyDetailObj"],
  data() {
    return {
      dataList: [],
      dataLoading: false,
      chartMode: false, // 图表模式
      summary: {}, // 汇总
      // 图表所需栏位定义
      chartProps: {
        // 代理数据
        agent: [
          {
            name: "实际使用卡数",
            prop: "userCardNum",
          },
          {
            name: "总收款",
            prop: "payAmount",
          },
          {
            name: "总出款",
            prop: "withdrawAmount",
          },
          {
            name: "总佣金",
            prop: "rebateTotal",
          },
        ],
        // 卡主数据
        customer: [
          {
            name: "总流水",
            prop: "transactionTotal",
          },
          {
            name: "收款",
            prop: "payAmount",
          },
          {
            name: "付款",
            prop: "withdrawAmount",
          },
          {
            name: "活动佣金",
            prop: "specialActivityBalanceAmount",
          },
          {
            name: "订单佣金",
            prop: "balanceAmount",
          },
          {
            name: "总佣金",
            prop: "rebateTotal",
          },
        ],
        // 客服绩效数据
        kefu: [
          {
            name: "总量",
            prop: "totalCount",
          },
          {
            name: "完成量",
            prop: "completedCount",
          },
          {
            name: "完成量大额",
            prop: "completedBigCount",
          },
          {
            name: "完成量小额",
            prop: "completedSmallCount",
          },
          {
            name: "未完成量",
            prop: "uncompletedCount",
          },
          {
            name: "失败量",
            prop: "failedCount",
          },
          {
            name: "退款量",
            prop: "refundCount",
          },
          {
            name: "审核前关闭量",
            prop: "closedCount",
          },
          {
            name: "平均完成时间",
            prop: "averageTime",
          },
          {
            name: "金额",
            prop: "amount",
          },
        ],
        // 审核员绩效数据
        credentialAuditor: [
          {
            name: "总量",
            prop: "totalCount",
          },
          {
            name: "确认出款",
            prop: "completionCount",
          },
          {
            name: "卡主未处理",
            prop: "uncompletionCount",
          },
          {
            name: "确认未出款",
            prop: "failCount",
          },
          {
            name: "审核前关闭",
            prop: "closeCount",
          },
          {
            name: "退款量",
            prop: "refundCount",
          },
          {
            name: "首次审核平均时间",
            prop: "firstAuditTime",
          },
          {
            name: "平均完成时间",
            prop: "averageTime",
          },
          {
            name: "离线次数",
            prop: "logOutCount",
          },
          {
            name: "转单次数",
            prop: "transferCount",
          },
        ],
      },
      chartOptions: {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            crossStyle: {
              color: "#409EFF",
            },
          },
          formatter: function (params) {
            let result = params[0].name + "<br />";
            params.forEach((item) => {
              result += `<span class="chart-label" style="background-color: ${item.color}"></span>${item.seriesName}: ${item.data}<br />`;
            });
            return result;
          },
        },
        dataZoom: [
          {
            type: "slider",
            bottom: 40, // 位置距离底部的距离
            fillerColor: "rgba(215, 231, 254, 0.4)",
            borderColor: "transparent",
            handleStyle: {
              color: "#000",
            },
            dataBackground: {
              lineStyle: {
                width: 0,
              },
            },
          },
        ],
        toolbox: {
          feature: {
            magicType: { show: true, type: ["line", "bar"] },
            saveAsImage: { show: true },
          },
          right: 75,
        },
        legend: {
          data: [],
          bottom: 0,
        },
        grid: {
          top: 50, // 图表与上方距离
          left: 80, // 图表与左方距离
          right: 80, // 图表与右方距离
          bottom: 150, // 图表与下方距离
        },
        offset: 0,
        xAxis: {
          data: [],
          axisLabel: { interval: 0, rotate: 40 },
        },
        yAxis: {},
        series: [],
      },
    };
  },
  watch: {
    dailyDetailObj(row) {
      if (row.visible) {
        this.getList();
      }
    },
    // 图表模式
    chartMode(visible) {
      if (visible) {
        this.$nextTick(() => {
          this.chartCreator(this.chartProps[this.dailyDetailObj.type]);
        });
      }
    },
  },
  computed: {
    dialogWidth() {
      switch (this.dailyDetailObj.type) {
        case "agent": // 代理数据
        case "customer": // 卡主数据
          return "900px";
        case "kefu": // 客服绩效数据
        case "credentialAuditor": // 审核员绩效数据
          return "1200px";
      }
    },
    // 展示汇总在图表模式
    summaryOnChartMode() {
      return this.chartProps[this.dailyDetailObj.type]
        .filter((item) =>
          Object.prototype.hasOwnProperty.call(this.summary, item.prop)
        )
        .map((item) => {
          if (["firstAuditTime", "averageTime"].includes(item.prop)) {
            // 若为 "平均完成时间"、"首次审核平均时间"，转换为时:分:秒格式
            item.time = this.formatDuration(this.summary[item.prop]);
          }
          item.value = this.formatDecimalSeparator(this.summary[item.prop], 4);
          return item;
        });
    },
  },
  methods: {
    // 创建图表
    chartCreator(items) {
      // 获取日期作为 x 轴
      this.chartOptions.xAxis.data = this.dataList.map((item) => item.date);

      this.chartOptions.series = items.map((res) => ({
        name: res.name,
        data: this.dataList.map((item) =>
          this.formatDecimalSeparator(item[res.prop], 4)
        ),
        type: "bar",
      }));

      this.chartOptions.legend.data = items.map((res) => res.name);

      // 创建图表
      echarts.init(this.$refs.mainChart).setOption(this.chartOptions, true);
    },
    closeDialog() {
      this.chartMode = false;
      this.dataList = [];
      this.$emit("update:daily-detail-obj", {});
    },
    getList() {
      const fetchData = (apiMethod, params) => {
        this.dataLoading = true;
        apiMethod(params)
          .then((res) => {
            this.dataList = res.data.infoList;
            this.summary = res.data.summary;
          })
          .finally(() => {
            this.dataLoading = false;
          });
      };

      const { startTime, endTime, username, id, orderType } =
        this.dailyDetailObj;

      switch (this.dailyDetailObj.type) {
        // 代理数据
        case "agent":
          fetchData(getAgentCountByDate, { startTime, endTime, username });
          break;

        // 卡主数据
        case "customer":
          fetchData(getDailyStatisticsByCustomer, {
            startTime,
            endTime,
            username,
          });
          break;

        // 客服绩效数据
        case "kefu":
          fetchData(getDailyStatisticsByKefu, {
            startTime,
            endTime,
            employeeId: id,
            orderType,
          });
          break;

        // 审核员绩效数据
        case "credentialAuditor":
          fetchData(getDailyStatisticsByAuditor, {
            startTime,
            endTime,
            auditorId: id,
          });
          break;
      }
    },
    // 表格汇总
    getSummaries(param) {
      const { columns } = param;
      const sums = [];

      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "区间数据汇总";
          return;
        }
        sums[index] = ["firstAuditTime", "averageTime"].includes(
          column.property
        ) // 若为 "平均完成时间"、"首次审核平均时间"，转换为时:分:秒格式
          ? this.formatDuration(this.summary[column.property])
          : this.formatDecimalSeparator(this.summary[column.property], 4);
      });
      return sums;
    },
  },
};
</script>

<style lang="scss" scoped>
.table-icon {
  width: 1.1rem;
}
.summary-tags {
  margin: 0 80px;
  .summary-tag {
    margin: 0 5px 5px 0;
    padding: 2px 10px;
    line-height: unset;
    height: unset;
    background-color: #eee;
    border-color: #eee;
    color: #333;
    font-size: 0.8rem;
    span {
      font-size: 1.1rem;
      font-weight: bold;
    }
  }
}

>>> {
  #daily-detail-dialog {
    .chart-label {
      display: inline-block;
      margin-right: 5px;
      border-radius: 10px;
      width: 10px;
      height: 10px;
    }
    .el-table {
      .el-table__footer {
        background-color: #f5f7fa;
        font-weight: 600;
      }
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/views/components/dialog/DailyDetail.vue